import { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import moment from 'moment';

import { SearchIcon } from 'src/icons';
import { useDebouncedValue } from 'src/hooks/misc';
import { useSearchPatients } from 'src/hooks/patient';

// Minimum characters before we hit the server. Kept in sync with the
// `useSearchPatients` hook which is disabled below this threshold.
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Debounced, per-organization patient search.
 *
 * Typing only updates local state and (after debounce) queries the org-scoped
 * `/v2/patients/search` endpoint. The encounter list filter (`searchFilters`)
 * is only updated when a patient is actually selected, so we no longer fire a
 * `listEncounters` network call on every keystroke.
 */
export const PatientSearchSelect = ({
  searchFilters,
  onChangePatientHandler,
  containerClassName = 'w-full mb-2 drop-shadow-sm bg-white rounded-xl',
  dropdownWidth = 232,
}) => {
  const [searchText, setSearchText] = useState(
    searchFilters?.patientName || ''
  );

  // Keep the input in sync when the selection is changed externally
  // (e.g. recent searches or the clear button).
  useEffect(() => {
    setSearchText(searchFilters?.patientName || '');
  }, [searchFilters?.patientName]);

  const debouncedSearch = useDebouncedValue(searchText, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();

  const { data, isFetching } = useSearchPatients({
    searchTerm: trimmedSearch,
  });

  const patientResults = useMemo(() => {
    return (data?.patients ?? []).map((patient) => ({
      id: patient.patient_Name,
      value: patient.patient_Name,
      patient_id: patient.patient_id,
      patient_dob: patient.patient_birth_date
        ? moment(patient.patient_birth_date).format('MM/DD/YYYY')
        : null,
      appointment_date:
        patient.next_appointment_date || patient.past_appointment_date
          ? moment(
              patient.next_appointment_date || patient.past_appointment_date
            ).format('MM/DD/YYYY')
          : null,
      ehr_id: patient.ehr_id,
    }));
  }, [data]);

  const showNoData =
    trimmedSearch.length >= MIN_SEARCH_LENGTH &&
    !isFetching &&
    patientResults.length === 0;

  const options = useMemo(() => {
    if (trimmedSearch.length < MIN_SEARCH_LENGTH || isFetching) {
      return [];
    }

    if (showNoData) {
      return [{ value: 'No data', label: 'No data', disabled: true }];
    }

    return patientResults.map((patient) => ({
      ...patient,
      key: patient.patient_id,
      value: `${patient.value}_${patient.patient_id}`,
      label: (
        <div className="w-full">
          <div>{patient.value}</div>
          <div className="flex flex-row justify-start gap-2">
            <div className="text-[11px] text-[#777777]">
              DOB: {patient.patient_dob}
            </div>
            <div className="text-[11px] text-[#777777]">
              Patient ID: {patient.patient_id}
            </div>
          </div>
          {patient?.ehr_id && (
            <div className="flex flex-row justify-start gap-2">
              <Tooltip
                title={patient.ehr_id.length > 15 ? patient.ehr_id : ''}
                className="text-[11px] text-[#777777]"
              >
                <div className="text-[11px] text-[#777777]">
                  EHR/MRN ID:{' '}
                  {patient.ehr_id.length > 15
                    ? `${patient.ehr_id.substring(0, 15)}...`
                    : patient.ehr_id}
                </div>
              </Tooltip>
            </div>
          )}
          {patient.appointment_date && (
            <div
              className={`text-[11px] ${
                moment(patient.appointment_date).isBefore(moment(), 'day')
                  ? 'text-[#ff0000]'
                  : 'text-[#16a878]'
              }`}
            >
              {moment(patient.appointment_date).isBefore(moment(), 'day')
                ? `Past appt.: ${patient.appointment_date}`
                : `Next appt.: ${patient.appointment_date}`}
            </div>
          )}
        </div>
      ),
    }));
  }, [patientResults, trimmedSearch, isFetching, showNoData]);

  const handleClear = () => {
    setSearchText('');
    onChangePatientHandler('', 'patientName', null, '');
  };

  return (
    <div className={containerClassName} id="PatientSearch">
      <AutoComplete
        variant="borderless"
        className="border-none font-sans h-10 text-base flex items-center w-full border-0 focus:outline-none placeholder-gray-500 pr-4"
        id="PatientSearchField"
        style={{ WebkitTextFillColor: 'gray' }}
        options={options}
        placeholder="Patient"
        filterOption={false}
        value={searchText}
        suffixIcon={
          searchFilters?.patientName ? (
            <CloseOutlined
              onClick={handleClear}
              style={{ cursor: 'pointer' }}
              className="hover:text-gray-700 transition-colors duration-200"
            />
          ) : (
            <SearchIcon />
          )
        }
        onSearch={(text) => setSearchText(text)}
        onSelect={(_value, option) => {
          if (option?.disabled) {
            return;
          }
          setSearchText(option.id);
          onChangePatientHandler(
            option.id,
            'patientName',
            null,
            option.patient_id
          );
        }}
        dropdownStyle={{ width: dropdownWidth }}
      />
    </div>
  );
};
